import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import type { BudgetSummary, Client, Project } from '@bpd/shared';
import { formatCurrencyRounded } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';
import { LedgerTable } from '../../components/LedgerTable.tsx';
import { GateBadges } from '../../components/projects/GateBadges.tsx';

type LoadState = 'loading' | 'ready' | 'error' | 'not-configured' | 'not-found';

export function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const location = useLocation();
  const cameFromFinancials = (location.state as { from?: string } | null)?.from === 'financials';
  const [state, setState] = useState<LoadState>('loading');
  const [client, setClient] = useState<Client | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [budget, setBudget] = useState<BudgetSummary | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  function load() {
    if (!clientId) return;
    if (!projectsApi.isConfigured()) {
      setState('not-configured');
      return;
    }
    setState('loading');
    Promise.all([
      projectsApi.listClients(),
      projectsApi.listProjects(clientId),
      projectsApi.getBudget(),
    ])
      .then(([clients, projectList, budgetSummary]) => {
        const found = clients.find((c) => c.id === clientId) ?? null;
        if (!found) {
          setState('not-found');
          return;
        }
        setClient(found);
        setProjects(projectList);
        setBudget(budgetSummary);
        setState('ready');
      })
      .catch(() => setState('error'));
  }

  useEffect(load, [clientId]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!clientId || !name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const created = await projectsApi.createProject({
        clientId,
        name: name.trim(),
        ...(description ? { description } : {}),
      });
      setProjects((prev) => [...prev, created]);
      setName('');
      setDescription('');
      setShowAdd(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add project');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteProject(id: string) {
    if (!clientId) return;
    await projectsApi.deleteProject(id, clientId);
    setProjects((prev) => prev.filter((p) => p.id !== id));
    setConfirmDeleteId(null);
  }

  return (
    <main className="mx-auto max-w-4xl px-6 py-16">
      <Link
        to={cameFromFinancials ? '/admin/financials' : '/admin/projects'}
        className="text-sm text-phoenix-blue-600 hover:underline"
      >
        ← {cameFromFinancials ? 'Financial Overview' : 'All clients'}
      </Link>

      {state === 'not-configured' && (
        <p className="mt-8 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          The project management backend isn&apos;t connected yet in this environment.
        </p>
      )}
      {state === 'not-found' && (
        <p className="mt-8 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Client not found.
        </p>
      )}
      {state === 'error' && (
        <p className="mt-8 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn&apos;t load this client. Try again in a moment.
        </p>
      )}
      {state === 'loading' && <p className="mt-8 text-sm text-gray-500">Loading…</p>}

      {state === 'ready' && client && (
        <>
          <div className="mt-4 flex items-center justify-between">
            <div>
              <h1 className="font-serif text-3xl font-bold text-phoenix-blue-900">
                {client.label}
              </h1>
              {(client.contactName || client.contactPhone || client.contactEmail) && (
                <p className="mt-1 text-sm text-gray-500">
                  {[client.contactName, client.contactPhone, client.contactEmail]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
              {(() => {
                const t = budget?.clientTotals.find((ct) => ct.clientId === clientId);
                if (!t || t.totalProjectCost <= 0) return null;
                return (
                  <div className="mt-2 max-w-xs">
                    <LedgerTable
                      rows={[
                        {
                          label: 'Total project cost',
                          value: formatCurrencyRounded(t.totalProjectCost),
                        },
                        {
                          label: 'Remaining balance due',
                          value: formatCurrencyRounded(t.remainingBalanceDue),
                          accent: true,
                        },
                      ]}
                    />
                  </div>
                );
              })()}
            </div>
            <button
              type="button"
              onClick={() => setShowAdd((v) => !v)}
              className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700"
            >
              {showAdd ? 'Cancel' : '+ Add project'}
            </button>
          </div>

          {showAdd && (
            <form
              onSubmit={(e) => void handleAdd(e)}
              className="mt-6 space-y-3 rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
            >
              <input
                type="text"
                placeholder="Project name — e.g. Gutters *"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                maxLength={200}
                required
              />
              <textarea
                placeholder="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                maxLength={4000}
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-phoenix-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
              >
                {saving ? 'Adding…' : 'Add project'}
              </button>
            </form>
          )}

          <div className="mt-6 space-y-2">
            {projects.length === 0 && (
              <p className="text-sm text-gray-400">No projects yet — add one above.</p>
            )}
            {projects.map((p) => {
              const pb = budget?.projects.find((b) => b.projectId === p.id);
              return (
                <div
                  key={p.id}
                  className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 shadow-sm transition hover:border-phoenix-blue-300 hover:shadow-md"
                >
                  <Link
                    to={`/admin/projects/clients/${clientId}/${p.id}`}
                    className="min-w-0 flex-1"
                  >
                    <p className="truncate font-semibold text-gray-900">{p.name}</p>
                    {p.description && (
                      <p className="truncate text-xs text-gray-500">{p.description}</p>
                    )}
                    {pb && (
                      <div className="mt-1">
                        <GateBadges gates={pb.gates} mode="compact" />
                      </div>
                    )}
                  </Link>

                  {confirmDeleteId !== p.id ? (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(p.id)}
                      className="shrink-0 text-sm text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  ) : (
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-xs text-gray-600">Delete?</span>
                      <button
                        type="button"
                        onClick={() => void handleDeleteProject(p.id)}
                        className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700"
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-xs text-gray-500 hover:underline"
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  <span className="flex shrink-0 gap-3 tabular-nums">
                    <span className="w-20 text-right font-bold text-gray-900">
                      {formatCurrencyRounded(pb?.totalProjectCost ?? 0)}
                    </span>
                    <span className="hidden w-20 text-right font-normal text-red-600 sm:inline-block">
                      {formatCurrencyRounded(pb?.remainingBalanceDue ?? 0)}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </main>
  );
}
